import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { notificationApi, billingQueueApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import NotificationBell from '../components/NotificationBell';
import { 
  LayoutDashboard, Users, Calendar, FileText, Building2,
  Settings, LogOut, Menu, X, ChevronDown, User,
  ClipboardList, DollarSign, UserPlus, FileCheck, AlertCircle, TrendingUp, Save, AlertTriangle, Wallet, CreditCard, Armchair, Activity, History, BarChart3, MessageCircle, Database, Truck, Inbox
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const MainLayout = () => {
  const { user, logout, isAdmin, isAdministration, isProjectManager, isTechnician, hasPermission, permissionsLoading } = useAuth();
  const { 
    isBlocking, 
    showDialog, 
    attemptNavigation, 
    confirmNavigation, 
    saveAndNavigate, 
    cancelNavigation 
  } = useNavigationGuard();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [billingCount, setBillingCount] = useState(0);

  // Gestione click sui link della sidebar con guard
  const handleNavClick = (e, href) => {
    // Se siamo già sulla stessa pagina, non fare nulla
    if (location.pathname === href) {
      e.preventDefault();
      return;
    }
    
    // Se c'è un blocco attivo, intercetta la navigazione
    if (isBlocking) {
      e.preventDefault();
      attemptNavigation(href);
    }
    // Altrimenti il Link funziona normalmente
  };

  // Carica notifiche
  useEffect(() => {
    loadNotifications();
    // Refresh ogni 30 secondi
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Badge "Richieste di fatturazione" da gestire (solo admin/amministrazione)
  useEffect(() => {
    if (!(isAdmin || isAdministration)) return;
    let active = true;
    const loadBilling = async () => {
      try {
        const res = await billingQueueApi.getStats();
        if (active) setBillingCount(res.data?.to_handle || 0);
      } catch (_) { /* best-effort */ }
    };
    loadBilling();
    const int = setInterval(loadBilling, 60000);
    return () => { active = false; clearInterval(int); };
  }, [isAdmin, isAdministration, location.pathname]);

  const loadNotifications = async () => {
    try {
      const res = await notificationApi.getAll(false);
      setNotifications(res.data.slice(0, 10));
      setUnreadCount(res.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationApi.markRead(id);
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_lead': return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'contract_pending': return <FileCheck className="w-4 h-4 text-amber-500" />;
      case 'contract_validated': return <FileCheck className="w-4 h-4 text-green-500" />;
      default: return <AlertCircle className="w-4 h-4 text-blue-500" />;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation items con permessi dinamici
  // Organizzati in sezioni con etichette
  const navigationSections = [
    {
      // Dashboard - senza etichetta (standalone)
      items: [
        { 
          name: 'Dashboard', 
          href: '/', 
          icon: LayoutDashboard,
          permissionKey: 'view_dashboard'
        },
      ]
    },
    {
      label: 'COMMERCIALE',
      items: [
        { 
          name: 'Lead', 
          href: '/leads', 
          icon: Users,
          permissionKey: 'view_leads'
        },
        { 
          name: 'Calendario', 
          href: '/calendar', 
          icon: Calendar,
          permissionKey: 'view_calendar'
        },
        { 
          name: 'Preventivi', 
          href: '/estimates', 
          icon: FileText,
          permissionKey: 'view_estimates'
        },
        { 
          name: 'Contratti', 
          href: '/contracts', 
          icon: FileCheck,
          permissionKey: 'view_contracts'
        },
      ]
    },
    {
      label: 'CANTIERI',
      items: [
        { 
          name: 'Progetti', 
          href: '/projects', 
          icon: Building2,
          permissionKey: 'manage_projects'
        },
        { 
          name: 'Arredi', 
          href: '/furniture', 
          icon: Armchair,
          permissionKey: 'manage_furniture'
        },
      ]
    },
    {
      label: 'CONTABILITÀ',
      items: [
        {
          name: 'Richieste di fatturazione',
          href: '/billing-queue',
          icon: Inbox,
          rolesAllowed: ['admin', 'administration'],
          badgeKey: 'billing',
        },
        { 
          name: 'Pagamenti', 
          href: '/payments', 
          icon: CreditCard,
          permissionKey: 'view_payments'
        },
        {
          name: 'Fornitori',
          href: '/admin/suppliers',
          icon: Truck,
          // Fornitori V1: visibile SOLO ad Admin e Administration.
          // I Project Manager continuano ad accedere all'anagrafica tramite
          // il wizard "Nuovo Subappalto" (Quick Create).
          rolesAllowed: ['admin', 'administration'],
        },
        { 
          name: 'Provvigioni', 
          href: '/commissions', 
          icon: Wallet,
          permissionKey: 'view_commissions'
        },
      ]
    },
    {
      label: 'IMPOSTAZIONI',
      items: [
        { 
          name: 'Listino Prezzi', 
          href: '/price-list', 
          icon: DollarSign,
          permissionKey: 'view_price_list'
        },
        { 
          name: 'Utenti', 
          href: '/users', 
          icon: User,
          permissionKey: 'manage_users'
        },
      ]
    },
    {
      label: 'ANALISI',
      items: [
        { 
          name: 'Statistiche', 
          href: '/stats', 
          icon: TrendingUp,
          permissionKey: 'view_stats'
        },
        { 
          name: 'Analytics', 
          href: '/analytics', 
          icon: BarChart3,
          permissionKey: 'view_stats'
        },
      ]
    },
    {
      // WhatsApp - senza etichetta (standalone in fondo)
      items: [
        { 
          name: 'WhatsApp', 
          href: '/whatsapp', 
          icon: MessageCircle,
          permissionKey: 'view_whatsapp'
        },
      ]
    },
    {
      label: 'SISTEMA',
      items: [
        { 
          name: 'System Health', 
          href: '/system-health', 
          icon: Activity,
          permissionKey: 'view_system_health'
        },
        { 
          name: 'Audit Log', 
          href: '/audit-logs', 
          icon: History,
          permissionKey: 'view_audit_logs'
        },
        { 
          name: 'Data Integrity', 
          href: '/data-integrity', 
          icon: Database,
          permissionKey: 'view_data_integrity'
        },
        { 
          name: 'Impostazioni', 
          href: '/settings', 
          icon: Settings,
          permissionKey: 'view_settings'
        },
      ]
    },
  ];

  // Filtra le sezioni e gli item in base ai permessi
  const filteredSections = navigationSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Se item ha rolesAllowed, filtro per ruolo (bypass matrice permessi)
        if (item.rolesAllowed) {
          return item.rolesAllowed.includes(user?.role);
        }
        return hasPermission(item.permissionKey);
      })
    }))
    .filter(section => section.items.length > 0);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="flex h-screen bg-[#FAFAFA]" data-testid="main-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 
        w-64 bg-white border-r border-[#DCDCDC]
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-[#DCDCDC]">
            <Link to="/" className="flex items-center">
              <img 
                src="/images/abitalo-logo.png" 
                alt="Abitalo" 
                className="h-8 object-contain"
              />
            </Link>
            <button 
              className="lg:hidden p-2 hover:bg-[#F2F2F2] rounded-lg"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-[#737373]" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3">
              {filteredSections.map((section, sectionIndex) => (
                <div key={sectionIndex} className={sectionIndex > 0 ? 'mt-4' : ''}>
                  {/* Section Label */}
                  {section.label && (
                    <div className="px-3 py-2">
                      <span className="text-[10px] font-semibold text-[#A0A0A0] uppercase tracking-wider">
                        {section.label}
                      </span>
                    </div>
                  )}
                  
                  {/* Section Items */}
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={(e) => handleNavClick(e, item.href)}
                          className={`nav-item ${active ? 'active' : ''}`}
                          data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                        >
                          <Icon className="w-5 h-5" />
                          <span>{item.name}</span>
                          {item.badgeKey === 'billing' && billingCount > 0 && (
                            <span
                              data-testid="billing-queue-badge"
                              className="ml-auto bg-[#FF7819] text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
                            >
                              {billingCount > 99 ? '99+' : billingCount}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </ScrollArea>

          {/* User Info */}
          <div className="p-4 border-t border-[#DCDCDC]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#FFF0E5] rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-[#FF7819]">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#3C3C3C] truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-[#737373] truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-[#737373] hover:text-[#CC0000] hover:bg-[#FFEDED]"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="glass-header h-16 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-[#F2F2F2] rounded-lg"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5 text-[#3C3C3C]" />
            </button>
            <h1 className="text-lg font-medium text-[#3C3C3C] hidden sm:block">
              {filteredSections.flatMap(s => s.items).find(item => isActive(item.href))?.name || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications - Real-time with SSE */}
            <NotificationBell />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-[#FFF0E5] rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-[#FF7819]">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#737373] hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-[#3C3C3C]">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-[#737373]">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')} 
                  className="cursor-pointer"
                  data-testid="profile-menu-item"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profilo
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-[#CC0000]">
                  <LogOut className="w-4 h-4 mr-2" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Navigation Guard Dialog - Mostrato quando si tenta di navigare con modifiche non salvate */}
      <AlertDialog open={showDialog} onOpenChange={cancelNavigation}>
        <AlertDialogContent className="max-w-sm sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              Modifiche non salvate
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              Hai delle modifiche non salvate. Cosa vuoi fare?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-row sm:gap-2 mt-4">
            <AlertDialogCancel 
              onClick={cancelNavigation} 
              className="w-full sm:w-auto order-3 sm:order-1"
            >
              Continua
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={confirmNavigation}
              className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 order-2"
            >
              Esci
            </Button>
            <AlertDialogAction
              onClick={saveAndNavigate}
              className="w-full sm:w-auto bg-[#FF7819] hover:bg-[#e06a15] order-1 sm:order-3"
            >
              <Save className="w-4 h-4 mr-1" />
              Salva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MainLayout;
